// src/components/common/AssistantFields.js
// Highlights fields an AI assistant (MCP or chatbot import) filled in, until the user confirms.
import React, { createContext, useContext } from "react";
import { Alert, Box, Chip } from "@mui/material";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";

export const AssistantFieldsContext = createContext([]);

const LABELS = {
  org_goal: "Use case goal", certification_needed: "Certification pathway",
  is_group_mode: "Single learner / group", job_title: "Job title", job_experience: "Job experience",
  education_background: "Education background", ldt_connection: "Connection to LDT projects",
  learning_motivation: "Learning motivation", future_roles: "Future roles", expected_contribution: "Expected contribution",
  education: "Education MLUs", governance: "Governance MLUs", technical: "Technical MLUs", ethics: "Ethics MLUs",
};

const LABEL = "Filled in by your assistant — please review";

/** The label on its own row, above the field it refers to (never inline with the field). */
function AssistantLabel() {
  return (
    <Box sx={{ display: "flex", mb: 0.75 }} data-assistant-filled="true">
      <Chip size="small" color="warning" variant="outlined" icon={<SmartToyOutlinedIcon />} label={LABEL}
        sx={{ maxWidth: "100%", height: "auto", py: 0.25, "& .MuiChip-label": { whiteSpace: "normal" } }} />
    </Box>
  );
}

/** Wrap one field: the label on its own row above, and a coloured outline around the field. */
export function AssistantMark({ field, children }) {
  const fields = useContext(AssistantFieldsContext);
  const fieldList = Array.isArray(field) ? field : [field];
  const active = fieldList.some((f) => fields.includes(f));
  if (!active) return children;
  return (
    <Box>
      <AssistantLabel />
      <Box sx={{ borderLeft: "4px solid", borderColor: "warning.main", pl: 1.5, borderRadius: 1, bgcolor: "rgba(255,193,7,0.06)" }}>
        {children}
      </Box>
    </Box>
  );
}

export function AssistantBanner({ fields, status }) {
  if (!fields?.length) return null;
  return (
    <Alert severity="warning" icon={<SmartToyOutlinedIcon />} sx={{ mb: 2 }} role="status">
      Your assistant filled in these fields: <strong>{fields.map((f) => LABELS[f] || f).join(", ")}</strong>.
      Please review each one, edit or clear it if needed, and press <strong>Confirm</strong>.
      {status === "proposed" ? " Nothing counts until you confirm." : ""}
    </Alert>
  );
}

/** Label only, on its own row (for compound controls such as sliders, chips and checkboxes). */
export function AssistantNote({ field }) {
  const fields = useContext(AssistantFieldsContext);
  const list = Array.isArray(field) ? field : [field];
  if (!list.some((f) => fields.includes(f))) return null;
  return <AssistantLabel />;
}
